import React, { useEffect, useState, useCallback, useMemo } from "react";
import TextStyles from "../../components/Elements/TextStyles/TextStyles";
import { ReactComponent as EditIcon } from "../../assets/svgs/greenEdit.svg";
import { SearchBar } from "../../components/Elements/Search/SearchBar";
import { API_URLS } from "../../api/apiUrls";
import { getApi } from "../../api/api_instance";
import { Tables } from "../../components/Elements/Tables/Tables";
import EditLicenseCostDialog from "./DialogBoxes/EditLicenseCost";
import PopupCard from "../../components/Cards/PopupCard/PopupCard";

const LicenceCost = () => {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedCost, setSelectedCost] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [popup, setPopup] = useState({
    visible: false,
    type: "", // "success" | "error" | "warning"
    title: "",
    message: "",
  });

  const showPopup = (type, title, message, duration = 3000) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => {
      setPopup({ visible: false, type: "", title: "", message: "" });
    }, duration);
  };

  const fetchLicenseCosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getApi({
        apiUrl: API_URLS.ALL_LICENSE_COSTS,
      });

      if (response.success && response.data) {
        setCosts(response.data);
      } else {
        setCosts([]);
      }
    } catch (error) {
      console.error("Error fetching license costs:", error);
      setCosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLicenseCosts();
  }, [fetchLicenseCosts]);

  const headers = useMemo(
    () => [
      { label: "Sr. No.", key: "serial" },
      { label: "License Type", key: "role" },
      { label: "Price per License (₹)", key: "price" },
      { label: "Action", key: "action" },
    ],
    []
  );

  const filteredCosts = useMemo(() => {
    if (!searchTerm.trim()) return costs;
    return costs.filter((item) =>
      item.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [costs, searchTerm]);

  const formattedData = useMemo(() => {
    return filteredCosts.map((item, index) => ({
      key: item._id,
      serial: index + 1,
      role: item.role,
      price: item.price !== undefined ? `₹${item.price}` : "₹0",
      action: (
        <div className="flex gap-4 items-center justify-center">
          <EditIcon
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedCost(item);
              setOpenEditDialog(true);
            }}
          />
        </div>
      ),
    }));
  }, [filteredCosts]);

  return (
    <div
      style={{ padding: "40px", backgroundColor: "#fff" }}
      className="h-screen"
    >
      <div className="flex justify-end text-xs font-light p-3">
        <p>Home</p>
        <span>/</span>
        <TextStyles color="gradient">Licence Cost</TextStyles>
      </div>
      <div>
        <div className="flex items-center justify-between mb-6 px-2">
          <div className="text-xl text-[#06513D] font-bold">
            Licence Cost 
          </div>
        </div>

        <div
          style={{
            border: "1px solid #3BA2B959",
            borderRadius: "10px",
            paddingTop: "20px",
            boxShadow: "0px 0px 16px 0px #3BA2B91F",
          }}
        >
          <div className="flex justify-between px-3 mb-3">
            <div className="flex gap-4 pl-9">
              <SearchBar width="300px" onSearch={(value) => setSearchTerm(value)} />
            </div>
          </div>

          <Tables
            data={formattedData}
            headers={headers}
            pageSize={10}
            loading={loading}
          />
        </div>
      </div>

      {openEditDialog && selectedCost && (
        <EditLicenseCostDialog
          onOpen={openEditDialog}
          onClose={() => {
            setOpenEditDialog(false);
            setSelectedCost(null);
          }}
          licenseData={selectedCost}
          onUpdate={() => {
            showPopup(
              "success",
              "Updated Successfully",
              "License Cost Updated Successfully"
            );
            fetchLicenseCosts();
          }}
          onError={(message) => {
            showPopup(
              "error",
              "Failed",
              message || "Failed to update License Cost"
            );
          }}
        />
      )}

      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
          />
        </div>
      )}
    </div>
  );
};

export default LicenceCost;
